import { Link } from "react-router-dom";
import { routesFor, type Lang } from "./primitives";

type Col = { title: string; links: { name: string; key: string }[] };

const COLUMNS: Record<Lang, Col[]> = {
  es: [
    {
      title: "Servicios",
      links: [
        { name: "SEO técnico", key: "serviceTechnical" },
        { name: "SEO local", key: "localSeoHub" },
        { name: "Google Ads", key: "serviceGoogleAds" },
        { name: "Rediseño web", key: "serviceWebsiteRedesign" },
        { name: "Branding", key: "serviceBranding" },
        { name: "Todos los servicios", key: "services" },
      ],
    },
    {
      title: "Trabajo",
      links: [
        { name: "Casos de éxito", key: "caseStudies" },
        { name: "Metodología", key: "methodology" },
        { name: "Sobre Luis ROC", key: "about" },
      ],
    },
    {
      title: "Recursos",
      links: [
        { name: "Biblioteca", key: "library" },
        { name: "Herramientas SEO", key: "seoTools" },
        { name: "Preguntas frecuentes", key: "faq" },
        { name: "Información oficial", key: "officialInfo" },
      ],
    },
    {
      title: "Contacto",
      links: [
        { name: "Reunión inicial gratuita — 30 min", key: "seoDiagnosis" },
        { name: "Contacto", key: "contact" },
        { name: "Agendar consultoría", key: "booking" },
      ],
    },
  ],
  en: [
    {
      title: "Services",
      links: [
        { name: "Technical SEO", key: "serviceTechnical" },
        { name: "Local SEO", key: "localSeoHub" },
        { name: "Google Ads", key: "serviceGoogleAds" },
        { name: "Website redesign", key: "serviceWebsiteRedesign" },
        { name: "Branding", key: "serviceBranding" },
        { name: "All services", key: "services" },
      ],
    },
    {
      title: "Work",
      links: [
        { name: "Case studies", key: "caseStudies" },
        { name: "Methodology", key: "methodology" },
        { name: "About Luis ROC", key: "about" },
      ],
    },
    {
      title: "Resources",
      links: [
        { name: "Library", key: "library" },
        { name: "SEO tools", key: "seoTools" },
        { name: "FAQ", key: "faq" },
        { name: "Official information", key: "officialInfo" },
      ],
    },
    {
      title: "Contact",
      links: [
        { name: "Free initial consultation — 30 min", key: "seoDiagnosis" },
        { name: "Contact", key: "contact" },
        { name: "Book a consultation", key: "booking" },
      ],
    },
  ],
};

const LEGAL: Record<Lang, { name: string; key: string }[]> = {
  es: [
    { name: "Términos de servicio", key: "terms" },
    { name: "Política de privacidad", key: "privacy" },
    { name: "Política de cookies", key: "cookies" },
    { name: "Aviso legal", key: "disclaimer" },
  ],
  en: [
    { name: "Terms of service", key: "terms" },
    { name: "Privacy policy", key: "privacy" },
    { name: "Cookies policy", key: "cookies" },
    { name: "Disclaimer", key: "disclaimer" },
  ],
};

export const SiteFooter = ({ language = "es" as Lang }: { language?: Lang }) => {
  const routes = routesFor(language) as unknown as Record<string, string>;
  const year = new Date().getFullYear();

  return (
    <footer
      className="border-t border-[color:var(--v3-line)] bg-[color:var(--v3-white)] py-14"
      aria-label={language === "es" ? "Pie de página" : "Footer"}
    >
      <div className="v3-shell">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <img
              src="/media/luis-roc/site-mark.png"
              alt="Luis ROC"
              className="h-8 w-auto"
              width={120}
              height={32}
              loading="lazy"
            />
            <p className="v3-note mt-4 text-[0.88rem]">ROC Worldwide Agency LLC</p>
            <p className="v3-note mt-2 text-[0.88rem]">
              9002 Six Pines Dr, Suite 277
              <br />
              Shenandoah, TX 77380
            </p>
            <p className="v3-note mt-2 text-[0.88rem]">
              <a href="tel:+18325215050" className="hover:underline">
                +1 832 521 5050
              </a>
              <br />
              <a href="mailto:luisroc@luisroc.com" className="hover:underline">
                luisroc@luisroc.com
              </a>
            </p>
          </div>

          {COLUMNS[language].map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <p className="v3-micro">{col.title}</p>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.key}>
                    <Link
                      to={routes[l.key]}
                      className="text-[0.93rem] text-[color:var(--v3-ink-soft)] hover:text-[color:var(--v3-blue)] hover:underline"
                    >
                      {l.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-[color:var(--v3-line)] pt-7 sm:flex-row sm:items-center sm:justify-between">
          <p className="v3-note text-[0.82rem]">
            © {year} ROC Worldwide Agency LLC.{" "}
            {language === "es"
              ? "Todos los derechos reservados."
              : "All rights reserved."}
          </p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            {LEGAL[language].map((l) => (
              <li key={l.key}>
                <Link
                  to={routes[l.key]}
                  className="text-[0.82rem] text-[color:var(--v3-muted)] hover:underline"
                >
                  {l.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};
